import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight, ChevronDown } from "lucide-react";

const LINKS = [
  { to: "/platform", label: "Platform", children: [
    { to: "/platform", label: "Platform overview" },
    { to: "/platform#manage", label: "Manage" },
    { to: "/platform#maintain", label: "Maintain" },
    { to: "/platform#understand", label: "Understand" },
    { to: "/platform#optimize", label: "Optimize" },
  ] },
  { to: "/solutions", label: "Solutions" },
  { to: "/plans", label: "Plans" },
  { to: "/insights", label: "Insights" },
  { to: "/company", label: "About" },
  { to: "/faq", label: "FAQ" },
];

const APP_URL = "https://app.anfordpim.com";
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setExpanded(null); }, [location]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <header data-testid="site-navbar" className={`sticky top-0 z-50 border-b backdrop-blur-xl transition-[background-color,border-color,box-shadow] duration-300 ${scrolled || open ? "border-navy/10 bg-white/95 shadow-sm" : "border-transparent bg-white/70"}`}>
      <div className="container-x flex h-[72px] items-center justify-between gap-6">
        <Link to="/" data-testid="nav-logo-link" className="flex shrink-0 items-center" aria-label="ANFORD home">
          <img src="/assets/anford-logo.png" alt="ANFORD. Manage. Protect. Grow." className="h-9 w-auto sm:h-10" width="120" height="40" />
        </Link>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <NavLink key={l.label} to={l.to} data-testid={`nav-link-${slug(l.label)}`} className={({ isActive }) => `font-display text-sm font-semibold tracking-tight transition-colors duration-200 hover:text-gold ${isActive ? "text-navy" : "text-navy-ink/70"}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <a href={APP_URL} data-testid="nav-login-link" target="_blank" rel="noopener noreferrer" className="font-display text-sm font-semibold text-navy transition-colors hover:text-gold">
            Login
          </a>
          <Link to="/demo" data-testid="nav-book-demo-button" className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 font-display text-sm font-semibold text-white transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-navy-deep hover:shadow-md">
            Book a Demo <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <button data-testid="nav-mobile-menu-button" className="-mr-2 flex h-11 w-11 items-center justify-center rounded-lg text-navy lg:hidden" onClick={() => setOpen((v) => !v)} aria-label="Toggle menu" aria-expanded={open}>
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="max-h-[calc(100dvh-72px)] overflow-y-auto border-t border-navy/10 bg-white px-5 pb-8 pt-3 lg:hidden">
          <nav className="flex flex-col" aria-label="Mobile">
            {LINKS.map((l) => (
              <div key={l.label} className="border-b border-navy/[0.07]">
                {l.children ? (
                  <>
                    <button type="button" data-testid={`mobile-nav-toggle-${slug(l.label)}`} onClick={() => setExpanded(expanded === l.label ? null : l.label)} aria-expanded={expanded === l.label} className="flex min-h-[52px] w-full items-center justify-between font-display text-base font-semibold text-navy-ink">
                      {l.label} <ChevronDown className={`h-5 w-5 text-navy/50 transition-transform duration-300 ${expanded === l.label ? "rotate-180" : ""}`} />
                    </button>
                    {expanded === l.label && (
                      <div className="flex flex-col pb-3">
                        {l.children.map((c) => (
                          <Link key={c.label} to={c.to} data-testid={`mobile-nav-sublink-${slug(c.label)}`} className="min-h-[44px] py-2.5 pl-4 text-sm font-medium text-slate2">
                            {c.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link to={l.to} data-testid={`mobile-nav-link-${slug(l.label)}`} className="flex min-h-[52px] items-center font-display text-base font-semibold text-navy-ink">
                    {l.label}
                  </Link>
                )}
              </div>
            ))}
          </nav>
          <div className="mt-6 flex flex-col gap-3">
            <Link to="/demo" data-testid="mobile-nav-book-demo-button" className="btn-primary w-full">
              Book a Demo <ArrowRight className="h-4 w-4" />
            </Link>
            <a href={APP_URL} data-testid="mobile-nav-login-link" target="_blank" rel="noopener noreferrer" className="flex min-h-[44px] items-center justify-center font-display text-sm font-semibold text-navy">
              Login to ANFORD
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
